import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function EstoquePanel() {
  const [dados, setDados] = useState(null);
  const [novo, setNovo] = useState(null);
  const [baixa, setBaixa] = useState(null);
  const [movsDe, setMovsDe] = useState(null);
  const [movs, setMovs] = useState([]);

  const load = useCallback(() => {
    axios.get(`${API}/financeiro/estoque`).then((r) => setDados(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const salvarNovo = async () => {
    try {
      await axios.post(`${API}/financeiro/estoque/item`, {
        produto: novo.produto, unidade: novo.unidade || "UN",
        quantidade: parseFloat(novo.quantidade) || 0,
        custo_unit: parseFloat(novo.custo_unit) || 0,
        minimo: parseFloat(novo.minimo) || 0,
      });
      toast.success("Produto adicionado 📦");
      setNovo(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao adicionar"); }
  };

  const salvarBaixa = async () => {
    try {
      const r = await axios.post(`${API}/financeiro/estoque/${baixa.id}/baixa`, { quantidade: parseFloat(baixa.qtd) || 0, obs: baixa.obs || "" });
      toast.success(r.data.alerta ? "Baixa feita — ⚠️ estoque no mínimo!" : "Baixa registrada ✅");
      setBaixa(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro na baixa"); }
  };

  const salvarMinimo = async (item, minimo) => {
    try {
      await axios.patch(`${API}/financeiro/estoque/${item.id}`, { minimo: parseFloat(minimo) || 0 });
      toast.success("Mínimo atualizado");
      load();
    } catch { toast.error("Erro ao salvar mínimo"); }
  };

  const excluir = async (item) => {
    if (!window.confirm(`Excluir "${item.produto}" do estoque?`)) return;
    try { await axios.delete(`${API}/financeiro/estoque/${item.id}`); load(); }
    catch { toast.error("Erro ao excluir"); }
  };

  const verMovs = async (item) => {
    if (movsDe === item.id) { setMovsDe(null); return; }
    try {
      const r = await axios.get(`${API}/financeiro/estoque/${item.id}/movs`);
      setMovs(r.data.movs || []); setMovsDe(item.id);
    } catch { toast.error("Erro ao carregar histórico"); }
  };

  const itens = dados?.itens || [];

  return (
    <div className="space-y-3" data-testid="estoque-panel">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-bold text-green-900 text-sm">📦 Estoque de Produtos</h3>
              <p className="text-[11px] text-gray-500">Compras por nota entram sozinhas. Baixa de uso não mexe no caixa.</p>
            </div>
            {dados?.alertas > 0 && <span className="text-xs font-bold bg-amber-100 text-amber-800 px-2 py-1 rounded-full" data-testid="estoque-alertas">⚠️ {dados.alertas} acabando</span>}
          </div>

          {itens.length === 0 && <p className="text-sm text-gray-400 text-center py-3">Estoque vazio. Lance uma nota de compra 📸 ou adicione manualmente.</p>}

          <div className="space-y-2" data-testid="estoque-lista">
            {itens.map((it) => (
              <div key={it.id} className={`border rounded-lg p-3 ${it.alerta ? "border-amber-400 bg-amber-50" : ""}`} data-testid={`estoque-item-${it.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{it.alerta ? "⚠️ " : ""}{it.produto}</p>
                    <p className="text-[11px] text-gray-500">{it.quantidade.toLocaleString("pt-BR")} {it.unidade}{it.custo_medio > 0 ? ` · custo médio ${BRL(it.custo_medio)}` : ""}{it.minimo > 0 ? ` · mín ${it.minimo}` : ""}</p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => setBaixa({ id: it.id, produto: it.produto, max: it.quantidade, qtd: "", obs: "" })} className="text-xs px-2.5 py-1.5 rounded-full bg-green-700 text-white font-semibold" data-testid={`estoque-baixa-btn-${it.id}`}>➖ Usar</button>
                    <button onClick={() => verMovs(it)} className="text-xs px-2 py-1.5 rounded-full bg-gray-100 text-gray-600" data-testid={`estoque-movs-btn-${it.id}`}>📜</button>
                    <button onClick={() => excluir(it)} className="text-xs px-2 py-1.5 rounded-full bg-gray-100 text-red-500" data-testid={`estoque-del-${it.id}`}>🗑</button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[10px] text-gray-500">Avisar quando restar:</span>
                  <input type="number" step="any" defaultValue={it.minimo || ""} onBlur={(e) => parseFloat(e.target.value) !== it.minimo && salvarMinimo(it, e.target.value)} className="border rounded px-2 py-0.5 text-xs w-20" data-testid={`estoque-minimo-${it.id}`} />
                  <span className="text-[10px] text-gray-400">{it.unidade}</span>
                </div>
                {movsDe === it.id && (
                  <div className="mt-2 border-t pt-2 space-y-1" data-testid={`estoque-movs-${it.id}`}>
                    {movs.length === 0 && <p className="text-[11px] text-gray-400">Sem movimentações.</p>}
                    {movs.map((m) => (
                      <p key={m.id} className="text-[11px] text-gray-600">
                        {m.tipo === "entrada" ? "⬆️" : "⬇️"} {m.quantidade} {it.unidade} · {new Date(`${m.date}T12:00:00`).toLocaleDateString("pt-BR")} · {m.origem === "nota" ? "📸 nota" : m.origem === "baixa" ? "uso" : "manual"}{m.obs ? ` — ${m.obs}` : ""}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {novo ? (
            <div className="border rounded-xl p-3 bg-gray-50 space-y-2" data-testid="estoque-novo-form">
              <Input placeholder="Produto (ex.: Protetor de chuva)" value={novo.produto || ""} onChange={(e) => setNovo({ ...novo, produto: e.target.value })} data-testid="estoque-novo-produto" />
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" step="any" placeholder="Quantidade" value={novo.quantidade || ""} onChange={(e) => setNovo({ ...novo, quantidade: e.target.value })} data-testid="estoque-novo-qtd" />
                <Input placeholder="Unidade (UN/KG/L)" value={novo.unidade || ""} onChange={(e) => setNovo({ ...novo, unidade: e.target.value })} data-testid="estoque-novo-un" />
                <Input type="number" step="any" placeholder="Custo unit. (R$)" value={novo.custo_unit || ""} onChange={(e) => setNovo({ ...novo, custo_unit: e.target.value })} data-testid="estoque-novo-custo" />
                <Input type="number" step="any" placeholder="Mínimo p/ alerta" value={novo.minimo || ""} onChange={(e) => setNovo({ ...novo, minimo: e.target.value })} data-testid="estoque-novo-minimo" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setNovo(null)} className="flex-1">Cancelar</Button>
                <Button onClick={salvarNovo} disabled={!novo.produto} className="flex-1 bg-green-700 hover:bg-green-800" data-testid="estoque-novo-salvar">Adicionar</Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setNovo({})} className="w-full border-dashed" data-testid="estoque-novo-btn">➕ Adicionar produto manualmente</Button>
          )}
        </CardContent>
      </Card>

      {baixa && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="estoque-baixa-modal">
          <div className="bg-white rounded-2xl p-4 w-full max-w-sm space-y-3">
            <h3 className="font-bold text-sm">➖ Usar: {baixa.produto}</h3>
            <p className="text-[11px] text-gray-500">Disponível: {baixa.max}. A baixa só desconta a quantidade — não mexe no caixa.</p>
            <Input type="number" step="any" placeholder="Quantidade usada" value={baixa.qtd} onChange={(e) => setBaixa({ ...baixa, qtd: e.target.value })} data-testid="estoque-baixa-qtd" />
            <Input placeholder="Observação (ex.: aplicação no talhão 2)" value={baixa.obs} onChange={(e) => setBaixa({ ...baixa, obs: e.target.value })} data-testid="estoque-baixa-obs" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setBaixa(null)} className="flex-1">Cancelar</Button>
              <Button onClick={salvarBaixa} disabled={!baixa.qtd} className="flex-1 bg-green-700 hover:bg-green-800" data-testid="estoque-baixa-confirmar">Confirmar baixa</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
